/**
 * /moto-ecoles/:dept — Page département SEO.
 * Liste des villes + top écoles labellisées + JSON-LD.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Award, ChevronRight, ArrowLeft, Home, ShieldCheck, Sparkles, ExternalLink } from 'lucide-react';
import { API, SITE_NAME, useSeoMeta, formatDateFR } from './_shared';

export default function MotoEcolesDept() {
  const { dept } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/annuaire/dept/${dept}`)
      .then(r => { if (!r.ok) throw new Error('not_found'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [dept]);

  const deptName = data?.dept_name || dept;
  const total = data?.total || 0;

  useSeoMeta({
    title: data ? `${total} moto-écoles en ${deptName} (${dept}) · ${SITE_NAME}` : `Département ${dept} · ${SITE_NAME}`,
    description: data ? `Liste des ${total} moto-écoles agréées du département ${deptName} (${dept}). Permis A, A1, A2 · Sources officielles Sécurité Routière.` : '',
    canonicalPath: `/moto-ecoles/${dept}`,
    jsonLd: data ? {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Moto-écoles en ${deptName} (${dept})`,
      numberOfItems: total,
      itemListElement: (data.villes || []).slice(0, 50).map((v, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: `Moto-écoles à ${v.ville}`,
        url: `https://code2roues.fr/moto-ecoles/${dept}/${v.slug}`,
      })),
    } : null,
  });

  if (loading) {
    return <div className="min-h-screen bg-[#0a0a0b] text-white/50 p-8 text-sm">Chargement…</div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] text-white p-8">
        <p className="text-white/60">Département introuvable ou vide.</p>
        <Link to="/moto-ecoles" className="mt-4 inline-flex items-center gap-2 text-rose-300 underline">← Retour à l'annuaire</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white pb-16" data-testid="moto-ecoles-dept">
      {/* Breadcrumb */}
      <div className="border-b border-white/10 bg-black/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 text-[12px] text-white/50 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-white"><Home className="w-3.5 h-3.5 inline" /></Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/moto-ecoles" className="hover:text-white">Annuaire</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white/80">{deptName} <span className="font-mono text-rose-300">({dept})</span></span>
        </div>
      </div>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <Link to="/moto-ecoles" className="inline-flex items-center gap-1 text-white/50 hover:text-white text-[12px] mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Tous les départements
        </Link>
        <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Moto-écoles en {deptName}
        </h1>
        <p className="text-white/60 mt-2 text-sm sm:text-base">
          <strong className="text-white">{total} écoles agréées</strong> pour la formation aux permis A, A1 et A2 · département <span className="font-mono text-rose-300">{dept}</span>.
        </p>
      </header>

      {/* Top écoles labellisées */}
      {data.top_schools && data.top_schools.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-8">
          <h2 className="text-white/70 text-[11px] font-black uppercase tracking-widest mb-3 flex items-center gap-2">
            <Award className="w-3.5 h-3.5 text-emerald-400" /> Écoles avec Label Qualité en {deptName}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.top_schools.slice(0, 9).map(s => {
              const isPartner = Boolean(s.featured_partner);
              return (
                <Link
                  key={s.id}
                  to={`/moto-ecoles/${dept}/${s.ville_slug}`}
                  className={`rounded-2xl p-4 transition group ${
                    isPartner
                      ? 'bg-gradient-to-br from-amber-500/[0.10] to-[#141416] border-2 border-amber-400/50 hover:border-amber-300 shadow-[0_0_0_1px_rgba(251,191,36,0.1)] hover:shadow-[0_8px_24px_-6px_rgba(251,191,36,0.35)]'
                      : 'bg-[#141416] border border-emerald-500/20 hover:border-emerald-500/50 hover:bg-[#181819]'
                  }`}
                  data-testid={`top-school-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className={`font-black leading-tight line-clamp-2 ${isPartner ? 'text-amber-50 text-[15px]' : 'text-white text-[14px]'}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{s.nom}</h3>
                      <p className={`text-[11px] mt-1 ${isPartner ? 'text-amber-200/70' : 'text-white/50'}`}>{s.ville}</p>
                    </div>
                    {isPartner ? (
                      <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/20 border border-amber-400/60 text-amber-200 text-[9px] font-black uppercase tracking-widest">
                        <Sparkles className="w-2.5 h-2.5" /> Partenaire
                      </span>
                    ) : (
                      <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9px] font-black uppercase tracking-widest">
                        <Award className="w-2.5 h-2.5" /> Qualité
                      </span>
                    )}
                  </div>
                  {/* Highlight (point fort partenaire) — box dorée très visible */}
                  {isPartner && s.highlight && (
                    <div className="mt-3 rounded-lg bg-amber-400/15 border border-amber-400/40 px-3 py-2 text-[12px] text-amber-100 font-semibold leading-snug" data-testid={`top-school-highlight-${s.id}`}>
                      {s.highlight}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-1 mt-2">
                    {(s.permis || []).map(p => (
                      <span key={p} className={`px-1.5 py-0.5 rounded-full text-[9px] font-black ${
                        isPartner ? 'bg-amber-500/15 border border-amber-400/40 text-amber-100' : 'bg-rose-500/15 border border-rose-500/30 text-rose-200'
                      }`}>{p}</span>
                    ))}
                    {isPartner && s.site_internet && (
                      <span className="ml-auto inline-flex items-center gap-1 text-amber-200/80 text-[10px] font-semibold group-hover:text-amber-100">
                        <ExternalLink className="w-3 h-3" /> Site web
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Grille villes */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="text-white/70 text-[11px] font-black uppercase tracking-widest mb-3">
          Villes du département — {data.villes.length} référencée{data.villes.length > 1 ? 's' : ''}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
          {data.villes.map(v => (
            <Link
              key={v.slug}
              to={`/moto-ecoles/${dept}/${v.slug}`}
              className="rounded-xl bg-[#141416] border border-white/10 hover:border-rose-500/40 hover:bg-[#181819] transition p-3 flex items-center justify-between gap-2"
              data-testid={`ville-tile-${v.slug}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-white text-[13px] font-bold truncate leading-tight">{v.ville}</div>
                <div className="text-white/40 text-[10.5px] mt-0.5">
                  {v.count} école{v.count > 1 ? 's' : ''}
                  {v.label_qualite_count > 0 && <span className="text-emerald-400 ml-1.5"><Award className="inline w-2.5 h-2.5" /> {v.label_qualite_count}</span>}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-white/30 shrink-0" />
            </Link>
          ))}
        </div>
      </section>

      {/* Texte SEO */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-10 space-y-3 text-white/70 text-[13.5px] leading-relaxed">
        <h2 className="text-white text-lg font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Passer son permis moto en {deptName}
        </h2>
        <p>
          Vous cherchez une <strong>moto-école agréée</strong> en {deptName} ({dept}) ? {SITE_NAME} référence <strong>{total} établissements</strong> répartis dans {data.villes.length} ville{data.villes.length > 1 ? 's' : ''} du département, à partir des données publiques de la Sécurité Routière (data.gouv.fr).
        </p>
        <p>
          Chaque fiche indique les <strong>catégories de permis dispensées</strong> (A, A1, A2), le <strong>Label Qualité des Formations</strong> lorsqu'il est délivré, ainsi que le taux de réussite officiel au permis A quand la donnée est disponible.
        </p>
        <p className="text-white/50 text-[12px]">
          <ShieldCheck className="inline w-3.5 h-3.5 mr-1 text-emerald-400" />
          Source : Sécurité Routière · <a href="https://www.data.gouv.fr/datasets/liste-des-auto-ecoles-et-taux-de-reussite-au-permis-de-conduire/" target="_blank" rel="noopener noreferrer" className="underline hover:text-rose-300">data.gouv.fr</a>
          {data.updated_at && <> · Mise à jour : {formatDateFR(data.updated_at)}</>}.
          <Link to="/mentions-annuaire" className="underline hover:text-rose-300 ml-1">Mentions légales et droits RGPD</Link>.
        </p>
      </section>
    </div>
  );
}
